/**
 * What the assistant says AFTER it has done the thing.
 *
 * The Altar assistant already saved: `customizePersonalAffirmation` and
 * `setPersonalAffirmation` both persist and fire `affirmation-updated`; the
 * avatar and altar scopes push a reshaped prompt into the builder. But the
 * chat only echoed the result in quotes and stopped.
 *
 * On a phone that is a dead end. The assistant is a full-screen sheet, so the
 * Altar it just changed is hidden behind it — there is no way to tell whether
 * anything was kept, and no obvious way back. The seeker is left looking at a
 * nice sentence with no next move.
 *
 * So every scope that COMMITS something states plainly that it was kept, and
 * offers the one action that follows: go look at it.
 */

/**
 * Two different things happen here, and saying "Kept" for both was wrong.
 *
 * The AFFIRMATION scope really does save — the text is persisted the moment it
 * appears. Every other scope only reshapes a DESCRIPTION; no image exists until
 * one is painted, and painting costs carats. Telling someone their avatar was
 * "updated" after they asked to paint it promises a picture that was never
 * made, and silently painting it would spend their carats without asking.
 *
 * So the description is shown to be READ, and painting is a separate,
 * deliberate press. `generates` names the subject to paint.
 */
const COMMITTED = {
  affirmation: {
    confirm: 'Kept. This is your affirmation for today.',
    cta: 'See it on your Altar',
    generates: null,
  },
  avatar: {
    confirm: 'Read it over. Painting costs carats, so nothing is made until you say so.',
    cta: 'Paint this',
    generates: 'avatar',
  },
  altar: {
    confirm: 'Read it over. Painting costs carats, so nothing is made until you say so.',
    cta: 'Paint this',
    generates: 'altar',
  },
  keeper: {
    confirm: 'Read it over. Painting costs carats, so nothing is made until you say so.',
    cta: 'Paint this',
    generates: 'keeper',
  },
  sun_sign: {
    confirm: 'Read it over, then paint when it feels right.',
    cta: 'Paint this', generates: 'sun_sign',
  },
  moon_sign: {
    confirm: 'Read it over, then paint when it feels right.',
    cta: 'Paint this', generates: 'moon_sign',
  },
  rising_sign: {
    confirm: 'Read it over, then paint when it feels right.',
    cta: 'Paint this', generates: 'rising_sign',
  },
  mystic_season: {
    confirm: 'Read it over, then paint when it feels right.',
    cta: 'Paint this', generates: 'mystic_season',
  },
}

/**
 * Returns `{ confirm, cta }` for a scope that commits, or null.
 *
 * Null for `name` and `journal` on purpose: the name picker already ends in
 * "Tap one to claim it", and the journal reply carries its own "Append to
 * entry". Both already tell you the next move, and stacking a second one would
 * only muddy it.
 */
export function assistOutcome(scope, { pricesVisible = true } = {}) {
  const o = COMMITTED[scope] || null
  // Before the Community opens a portrait is free: no talk of carats.
  if (o && !pricesVisible && /carats/.test(o.confirm)) return { ...o, confirm: 'Read it over, then paint when it feels right.' }
  return o
}

export function assistCommits(scope) {
  return assistOutcome(scope) !== null
}
